import { useEffect } from 'react'
import { useRouter } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { EmptyState, Button } from '@/components'
import { webAppState } from '@/appState'

import ErrorIllustration from './ErrorIllustration.svg'

const setLocalStorageWithExpiry = (key: string, value: string, ttl: number) => {
    const item = {
        value: value,
        expiry: new Date().getTime() + ttl,
    }
    localStorage.setItem(key, JSON.stringify(item))
}

const getLocalStorageWithExpiry = (key: string) => {
    const itemString = window.localStorage.getItem(key)
    if (!itemString) {
        return null
    }

    const item = JSON.parse(itemString)
    const isExpired = new Date().getTime() > item.expiry

    if (isExpired) {
        localStorage.removeItem(key)
        return null
    }

    return item.value
}

const moduleFailedMessageRegexes = [
    /Loading chunk [\d]+ failed/,
    /Failed to fetch dynamically imported module: (.*)/,
    /error loading dynamically imported module: (.*)/,
]

export const ErrorFallback = ({ error, eventId }: { error: unknown; eventId?: string }) => {
    const { history } = useRouter()

    const { t } = useTranslation()

    const i18nKey = 'app.error'

    // Handle failed lazy loading of a JS/CSS chunk
    useEffect(() => {
        if (
            error instanceof Error &&
            error.message &&
            moduleFailedMessageRegexes.find((regex) => regex.test(error.message))
        ) {
            if (!getLocalStorageWithExpiry('chunk_failed')) {
                setLocalStorageWithExpiry('chunk_failed', 'true', 10000)
                window.location.reload()
            }
        }
    }, [error])

    return (
        <div className="flex h-full min-h-full flex-col items-center justify-center gap-8">
            <div className="m-4 flex flex-col items-center justify-center">
                <EmptyState
                    layout="container"
                    illustrationSrc={ErrorIllustration}
                    title={t(`${i18nKey}.title`)}
                    subtitle=""
                />
                {eventId && !webAppState.isLocalhost && (
                    <div className="text-secondary text-center text-xs">
                        {t(`${i18nKey}.event`, { event: eventId })}
                    </div>
                )}
                {webAppState.isLocalhost && error instanceof Error && (
                    <div className="text-secondary text-xxs m-4 max-w-4xl whitespace-pre-line rounded-md border p-3 font-mono">
                        {error.stack || error.message}
                    </div>
                )}
            </div>
            <div className="flex flex-col items-center gap-8">
                <Button
                    block
                    variant="default"
                    onClick={() => {
                        history.back()
                        setTimeout(() => {
                            window.location.reload()
                        }, 500)
                    }}
                >
                    {t(`${i18nKey}.goBack`)}
                </Button>
            </div>
        </div>
    )
}
